import { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Pagination,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import type { SelectChangeEvent } from '@mui/material/Select';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link as RouterLink } from 'react-router-dom';

import { adminApi, type AdminAdvertiser, type AdvertiserStatus } from '../../services/adminApi';

const STATUS_FILTERS: { value: '' | AdvertiserStatus; label: string }[] = [
  { value: '', label: 'All statuses' },
  { value: 'pending', label: 'Pending' },
  { value: 'active', label: 'Active' },
  { value: 'suspended', label: 'Suspended' },
];
const PAGE_SIZE = 25;

const STATUS_COLOR: Record<AdvertiserStatus, 'warning' | 'success' | 'default'> = {
  pending: 'warning',
  active: 'success',
  suspended: 'default',
};

/**
 * Admin advertiser-management index page (GitHub #12 area 3).
 *
 * Lists advertiser accounts with a search box and status filter, and lets an
 * admin approve a pending account or suspend / re-activate one inline. Auth is
 * enforced upstream by AdminLayout (redirects non-admins); the server also
 * re-checks admin role.
 *
 * Privacy / scope (spec §5/§17.3): account administration only — the list shows
 * company name, contact email, status, and campaign count. No payment card data
 * is displayed here or anywhere in this admin surface.
 */
export function AdvertisersListPage() {
  const queryClient = useQueryClient();

  const [searchDraft, setSearchDraft] = useState('');
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState<'' | AdvertiserStatus>('');
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: ['admin', 'advertisers', { search, status, page }],
    queryFn: () =>
      adminApi.listAdvertisers({
        search: search || undefined,
        status: status || undefined,
        page,
        limit: PAGE_SIZE,
      }),
  });

  const mutation = useMutation({
    mutationFn: (vars: { id: string; status: AdvertiserStatus }) =>
      adminApi.patchAdvertiser(vars.id, { status: vars.status }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['admin', 'advertisers'] });
    },
  });

  function onSearchSubmit(event: React.FormEvent) {
    event.preventDefault();
    setSearch(searchDraft.trim());
    setPage(1);
  }

  function onStatusFilterChange(event: SelectChangeEvent<string>) {
    setStatus(event.target.value as '' | AdvertiserStatus);
    setPage(1);
  }

  function onApprove(advertiser: AdminAdvertiser) {
    mutation.mutate({ id: advertiser.id, status: 'active' });
  }

  function onToggleSuspend(advertiser: AdminAdvertiser) {
    const next: AdvertiserStatus = advertiser.status === 'suspended' ? 'active' : 'suspended';
    mutation.mutate({ id: advertiser.id, status: next });
  }

  return (
    <Box>
      <Typography variant="h1" sx={{ fontSize: '1.6rem', mb: 2 }}>
        Advertiser management
      </Typography>

      <Paper
        component="form"
        onSubmit={onSearchSubmit}
        sx={{ p: 2, mb: 2, display: 'flex', gap: 1, alignItems: 'flex-end', flexWrap: 'wrap' }}
        elevation={0}
        variant="outlined"
      >
        <TextField
          id="advertiser-search"
          label="Search by company or email"
          size="small"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
          sx={{ minWidth: 280 }}
        />
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel id="advertiser-status-filter-label">Status</InputLabel>
          <Select
            labelId="advertiser-status-filter-label"
            id="advertiser-status-filter"
            value={status}
            label="Status"
            onChange={onStatusFilterChange}
          >
            {STATUS_FILTERS.map((s) => (
              <MenuItem key={s.value || 'all'} value={s.value}>
                {s.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button type="submit" variant="contained">
          Search
        </Button>
      </Paper>

      {mutation.isError && (
        <Alert severity="error" role="alert" sx={{ mb: 2 }}>
          Update failed: {(mutation.error as Error).message}
        </Alert>
      )}

      {query.isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
          <CircularProgress aria-label="Loading advertisers" />
        </Box>
      )}

      {query.isError && (
        <Alert severity="error" role="alert">
          Failed to load advertisers: {(query.error as Error).message}
        </Alert>
      )}

      {query.isSuccess && (
        <>
          <TableContainer component={Paper} variant="outlined">
            <Table aria-label="Advertisers">
              <TableHead>
                <TableRow>
                  <TableCell>Company</TableCell>
                  <TableCell>Contact email</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Campaigns</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {query.data.advertisers.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6}>
                      <Typography variant="body2" color="text.secondary">
                        No advertisers match the current filters.
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  query.data.advertisers.map((advertiser) => (
                    <TableRow key={advertiser.id} hover>
                      <TableCell>
                        <MuiLink component={RouterLink} to={`/admin/advertisers/${advertiser.id}`}>
                          {advertiser.companyName}
                        </MuiLink>
                      </TableCell>
                      <TableCell>{advertiser.contactEmail}</TableCell>
                      <TableCell>
                        <Chip
                          label={advertiser.status}
                          color={STATUS_COLOR[advertiser.status]}
                          size="small"
                          sx={{ textTransform: 'capitalize' }}
                        />
                      </TableCell>
                      <TableCell>{advertiser.campaignCount ?? 0}</TableCell>
                      <TableCell>{advertiser.createdAt ?? '—'}</TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {advertiser.status === 'pending' && (
                            <Button
                              size="small"
                              variant="outlined"
                              color="success"
                              disabled={mutation.isPending}
                              onClick={() => onApprove(advertiser)}
                              aria-label={`Approve ${advertiser.companyName}`}
                            >
                              Approve
                            </Button>
                          )}
                          <Button
                            size="small"
                            variant="outlined"
                            color={advertiser.status === 'suspended' ? 'success' : 'warning'}
                            disabled={mutation.isPending}
                            onClick={() => onToggleSuspend(advertiser)}
                            aria-label={`${
                              advertiser.status === 'suspended' ? 'Reactivate' : 'Suspend'
                            } ${advertiser.companyName}`}
                          >
                            {advertiser.status === 'suspended' ? 'Reactivate' : 'Suspend'}
                          </Button>
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
            <Pagination
              count={Math.max(1, Math.ceil(query.data.pagination.total / PAGE_SIZE))}
              page={page}
              onChange={(_e, p) => setPage(p)}
              aria-label="Advertisers pagination"
            />
          </Box>
        </>
      )}
    </Box>
  );
}

export default AdvertisersListPage;
